import React, { useState, useEffect } from 'react';
import {
  ComparisonViewModel,
  FilterSessionStatus,
  GetFilterSessionResponse,
  GetGenericSizingResultsResponse,
  Part,
  ProductCacheStatus,
  ProductSeriesFilterResult,
} from '../../models/Api';
import SizingToolService from '../../services/SizingToolService';
import { useTranslation } from 'react-i18next';

//styles
import styles from './SizingTools.module.scss';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Checkbox, LinearProgress, LinearProgressProps, TextField } from '@mui/material';
import { toast } from 'react-toastify';
import SystemService from '../../services/SystemService';
import { getBasePath } from '../../utils/Utils';

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant="determinate" {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

export default function ResultsPanel(props: {
  part: Part;
  isMetric: boolean;
  hasCompareContainer: boolean;
  productCacheStatus: ProductCacheStatus;
  selectAllProductsToCompare: boolean;
  seriesNameFilterExample: string;
  previousProductSeriesResults: ProductSeriesFilterResult[];
  previousProductSeriesResultsToCompare: ProductSeriesFilterResult[];
  handleSaveProgress: () => void;
  handleCompare: (selectedSeries: ProductSeriesFilterResult[]) => void;
  resetSizingRequest: () => void;
  validateForm: (enableTouched?: boolean) => boolean | undefined;
  scrollErrorIntoView: () => void;
  handleGenerateResults: (loggingEnabled: boolean, seriesNameFilter: string) => Promise<boolean>;
  handleReceivedResults: (productSeriesResults: ProductSeriesFilterResult[], comparisonViewModel: ComparisonViewModel) => void;
  handleProductSeriesResultsToCompare: (productsToCompare: ProductSeriesFilterResult[]) => void;
  handleDownloadSizingDebuggingInfo?: () => void;
  renderInvalidFieldsErrorToast: () => void;
  clearProductSeriesResults: () => void;
}) {
  const {
    part,
    isMetric,
    hasCompareContainer,
    productCacheStatus,
    selectAllProductsToCompare,
    seriesNameFilterExample,
    previousProductSeriesResults,
    previousProductSeriesResultsToCompare,
    handleSaveProgress,
    handleCompare,
    resetSizingRequest,
    validateForm,
    scrollErrorIntoView: scrollIntoView,
    handleGenerateResults,
    handleReceivedResults,
    clearProductSeriesResults,
    handleProductSeriesResultsToCompare,
    handleDownloadSizingDebuggingInfo,
    renderInvalidFieldsErrorToast,
  } = props;

  const { t } = useTranslation();
  const [loggingEnabled, setLoggingEnabled] = useState(false);
  const [productsList, setProductsList] = useState<ProductSeriesFilterResult[] | null | undefined>([]);

  const [progress, setProgress] = useState({ percentComplete: 0, label: '' });
  const [showLoadingProgress, setShowLoadingProgress] = useState(false);
  const [seriesNameFilter, setSeriesNameFilter] = useState('');
  const [productsToCompare, setProductsToCompare] = useState<ProductSeriesFilterResult[]>([]);
  const [formIsValid, setFormIsValid] = useState<boolean | undefined>(false);
  const [resultsContainerErrorMessage, setResultsContainerErrorMessage] = useState('');
  const [downloadLogFileUrl, setDownloadLogFileUrl] = useState('');

  const getFilterProgressLabel = (response: GetFilterSessionResponse | undefined) => {
    const filterSession = response?.filterSession;
    const filterProgressLabel = `${filterSession?.statusMessage} (${filterSession?.processStepNumber}/${filterSession?.processStepTotal}) ${filterSession?.productCount}/${filterSession?.productTotal}`;
    return filterProgressLabel;
  };

  const pollSizingFilterSessionDetail = async (endTime: number) => {
    if (endTime < 0) {
      toast.error(t('FilterSessionTimeoutError') as string);
      setShowLoadingProgress(false);
      return;
    }

    const response = await SizingToolService.getSizingFilterSessionDetail(part.workflowId as string);
    if (response?.filterSession?.errorMessage) {
      setResultsContainerErrorMessage(
        process.env.REACT_APP_IS_PRODUCTION === 'true' ? t('UnexpectedError') : t('UnexpectedError') + ': ' + response.filterSession.errorMessage,
      );
      return;
    }

    setProgress({
      percentComplete: response?.filterSession?.percentComplete as number,
      label: getFilterProgressLabel(response),
    });
    if (response?.filterSession?.status === FilterSessionStatus.Complete) {
      const partCategoryName = SystemService.getCurrentState()?.system?.partCategories?.find((category) => category.id === part.categoryId)?.name;
      const readyToGetGenericResults = await SizingToolService.compareApiHelper(part, partCategoryName as string, isMetric);
      if (readyToGetGenericResults.status === 200) {
        const resultsDetail = await SizingToolService.getSizingGenericResultsDetail(part.workflowId as string);
        const genericSizingResultsResponse = resultsDetail as GetGenericSizingResultsResponse;
        // The value of the RawFeedback property coming from the Filter service will have an anchor <a> HTML tag in it,
        // so let's extract the URL from it and build it properly
        if (genericSizingResultsResponse.compareResultsContainer?.rawFeedback) {
          const sizingUrl = `${process.env.REACT_APP_LEGACY_BASE_HOST}/Sizing/`;
          const hrefMatch = genericSizingResultsResponse.compareResultsContainer?.rawFeedback.match(/href=["']([^"']+)["']/);
          const logUrl = hrefMatch ? hrefMatch[1] : null;
          if (logUrl) {
            setDownloadLogFileUrl(logUrl.replace('{SIZINGURLTOKEN}', sizingUrl));
          }
        }
        if (resultsDetail?.productSeriesResults?.length === 0) {
          setResultsContainerErrorMessage(t('NoResultsFound'));
          clearProductSeriesResults();
        } else if (resultsDetail?.compareResultsContainer?.tooManyResults) {
          setResultsContainerErrorMessage(t('TooManyResults'));
          clearProductSeriesResults();
        } else {
          const productSeriesResults = genericSizingResultsResponse.productSeriesResults as ProductSeriesFilterResult[];
          const comparisonViewModel = genericSizingResultsResponse.compareResultsContainer?.compareViewModel as ComparisonViewModel;
          setProductsList(productSeriesResults);
          handleReceivedResults(productSeriesResults, comparisonViewModel);
        }
      }
      return;
    }

    setTimeout(() => {
      pollSizingFilterSessionDetail(endTime - 1);
    }, 1000);
  };

  const updateSeriesNameFilter = (partialSeriesName: string) => {
    setSeriesNameFilter(partialSeriesName);
    const key = `${part.categoryId}SizingToolSeriesNameFilter`;
    const value = encodeURIComponent(partialSeriesName);
    const maxAge = partialSeriesName ? '60*60*24*365' : 0; // 1 year
    const path = getBasePath();
    document.cookie = `${key}=${value};max-age=${maxAge};path=${path};SameSite=strict;secure`;
  };

  useEffect(() => {
    setFormIsValid(validateForm());
    if (previousProductSeriesResults) {
      setProductsList(previousProductSeriesResults);
    }
    if (previousProductSeriesResultsToCompare.length) {
      setProductsToCompare(previousProductSeriesResultsToCompare);
    }
  }, [props]);

  useEffect(() => {
    if (selectAllProductsToCompare && !previousProductSeriesResultsToCompare.length) {
      setProductsToCompare(productsList as ProductSeriesFilterResult[]);
    }

    if (productsList && productsList.length > 0) setShowLoadingProgress(false);
  }, [productsList]);

  useEffect(() => {
    const key = `${part.categoryId}SizingToolSeriesNameFilter`;
    const partialSeriesName =
      document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${key}=`))
        ?.split('=')[1] || '';
    setSeriesNameFilter(partialSeriesName);
  }, []);

  const renderSelectAllCheckbox = () => {
    return (
      <div className={styles['select-all-checkbox-div']}>
        <Checkbox
          color="info"
          onChange={(e) => {
            const checkboxElement = e.target as HTMLInputElement;
            if (checkboxElement.checked) {
              setProductsToCompare(productsList as ProductSeriesFilterResult[]);
            } else {
              setProductsToCompare([]);
            }
          }}
          checked={productsList?.length === productsToCompare.length}
          indeterminate={(productsList?.length as number) > 0 && productsToCompare.length > 0 && productsToCompare.length !== productsList?.length}
        />
        <label>{t('SelectAll')}</label>
      </div>
    );
  };

  return (
    <div className={styles['results-container']}>
      <div className={styles['buttons-container']}>
        <div
          onClick={async () => {
            if (!validateForm(true)) return;
            clearProductSeriesResults();
            setShowLoadingProgress(true);
            setResultsContainerErrorMessage('');
            handleProductSeriesResultsToCompare([]);
            const started = await handleGenerateResults(loggingEnabled, seriesNameFilter);
            if (started) pollSizingFilterSessionDetail(900);
          }}
          className={
            styles[formIsValid && productCacheStatus === ProductCacheStatus.Populated ? 'generate-results-button-enabled' : 'generate-results-button-disabled']
          }
        >
          {t('GenerateResults')}
        </div>
        <div
          onClick={() => {
            handleCompare(productsToCompare);
            handleProductSeriesResultsToCompare(productsToCompare);
          }}
          className={styles[productsToCompare.length > 0 && hasCompareContainer ? 'compare-button-enabled' : 'compare-button-disabled']}
        >
          {t('Compare')}
        </div>
        <div
          onClick={() => {
            if (!validateForm(true)) renderInvalidFieldsErrorToast();
            scrollIntoView();
            handleSaveProgress();
          }}
          className={styles['save-progress-button']}
        >
          {t('SaveProgress')}
        </div>
        <div onClick={resetSizingRequest} className={styles['reset-parameters-button']}>
          {t('ResetParameters')}
        </div>
        {process.env.REACT_APP_IS_PRODUCTION === 'false' && handleDownloadSizingDebuggingInfo && (
          <div onClick={() => handleDownloadSizingDebuggingInfo()} className={styles['download-sizing-debug-button']}>
            {t('DownloadSizingDebuggingInfo')}
            <form id="downloadSizingDebugInfo" method="POST" action={`${process.env.REACT_APP_LEGACY_BASE_HOST}/Sizing/DownloadDebugInfo`}>
              <input type="hidden" id="sizingDebugInfo" name="debugInfo" />
              <input type="hidden" id="sizingDebugFileName" name="fileName" />
            </form>
          </div>
        )}
        {downloadLogFileUrl && (
          <div className={styles['download-log-button']}>
            <a href={downloadLogFileUrl} target="_blank" rel="noreferrer">
              {t('DownloadResultsGenerationLog')}
            </a>
          </div>
        )}
      </div>
      {process.env.REACT_APP_IS_PRODUCTION === 'false' && (
        <>
          <div className={styles['logging-checkbox-div']}>
            <Checkbox color="info" id="loggingEnabled" onChange={() => setLoggingEnabled(!loggingEnabled)} />
            <label htmlFor="loggingEnabled">{t('EnableResultsGenerationLog')}</label>
          </div>

          <div className={styles['filter-field-container']}>
            <div className={styles['filter-field-label']}>
              {t('SeriesNameFilterLabel')}
              {seriesNameFilterExample}
            </div>

            <TextField
              id="seriesNameFilter"
              placeholder={t('WillAffectFutureResults')}
              onChange={(e) => updateSeriesNameFilter(e.target.value)}
              value={seriesNameFilter}
              className={styles['filter-field']}
            />
          </div>
        </>
      )}
      <div className={styles['results-list-container']}>
        {showLoadingProgress && (
          <div className={styles['loading-progress']}>
            {<LinearProgressWithLabel value={progress.percentComplete} />}
            {<span>{progress.label}</span>}
          </div>
        )}
        {resultsContainerErrorMessage && (
          <div className={styles['error-message-div']}>
            <p>{resultsContainerErrorMessage}</p>
          </div>
        )}

        {productsList && productsList.length > 0 && !resultsContainerErrorMessage && hasCompareContainer && renderSelectAllCheckbox()}

        {productsList &&
          productsList?.length > 0 &&
          !resultsContainerErrorMessage &&
          productsList.map((productDetail: ProductSeriesFilterResult) => (
            <div className={styles['product-card-div']} key={productDetail.seriesId}>
              <div className={styles['product-details-wrapper']}>
                <div className={styles['product-image-container']}>
                  <img src={productDetail.seriesThumbnailUrl ?? ''} />
                </div>
                <div className={styles['product-details-container']}>
                  <p className={styles['product-name']}>
                    {productDetail.seriesName} ({productDetail.productResultsCount} {t('Products')})
                  </p>
                  <div className={styles['product-description']}>{productDetail.seriesShortDescription}</div>
                  <div className={styles['product-info-button']}>
                    <a href={productDetail.seriesProductInfoUrl ?? 'https://www.parker.com/'} target="_blank" rel="noreferrer">
                      {t('ProductInfo')}
                    </a>
                  </div>
                </div>
              </div>

              {hasCompareContainer && (
                <div className={styles['include-in-compare-container']}>
                  <div className={styles['include-checkbox-div']}>
                    <Checkbox
                      color="info"
                      onChange={(e) => {
                        const checkboxElement = e.target as HTMLInputElement;
                        if (checkboxElement.checked) {
                          setProductsToCompare([...productsToCompare, productDetail]);
                        } else {
                          if (productsToCompare) {
                            const filteredProducts = productsToCompare.filter((product) => product.seriesId !== productDetail.seriesId);
                            setProductsToCompare(filteredProducts);
                          }
                        }
                      }}
                      checked={productsToCompare.some((product) => product.seriesId === productDetail.seriesId)}
                    />
                    <label>{t('IncludeInCompare')}</label>
                  </div>
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
